// ────────────────────────────────────────────────────────────────────
// JobScreen — one งาน's workspace, at /jobs/:id.
//
// This is SubmissionsScreen's right half, lifted out unchanged (spec:
// docs/superpowers/specs/2026-09-01-today-is-the-queue-design.md). That screen
// described itself as master-detail — "left = the queue, right = the selected
// submission's workspace" — and the two halves answer different questions:
// the queue is "what should I be doing" (which becomes TODAY), the workspace
// is "what is happening on THIS job". Only the second one deserves an address.
//
// Everything the workspace needs lives here now — the status vocabulary
// (STATUS_META/StatusPill), the pipeline, the transition labels, the event
// renderer — because the workspace is what those describe. SubmissionsScreen
// imports them back for its queue rows while it still exists; GuestLanding
// reads them for its read-only conversation.
//
// Role-aware from the backend's allowed_transitions:
//   • client — opens work, submits, cancels, resumes after "needs info"
//   • staff  — acknowledges, works, asks for info, completes with an outcome
// STAFF-ONLY as a screen: the workspace is the team's tool, and a customer is
// sent to the file room (the gate that lived at SubmissionsScreen:150).
// ────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FileText, ExternalLink, CheckCircle2, Building2, ArrowRight, ArrowLeft, Send, ChevronDown, ChevronRight, Pencil, X } from "lucide-react";
import { apiClient, getCachedUser, STAFF_ROLES } from "@/lib/api";
import { tint } from "@/lib/utils";
import type { SubmissionDetail, SubmissionStatus, SubmissionEvent, IntakeFile } from "@/lib/types";
import { GuestInvitePanel } from "@/components/GuestInvitePanel";

// Thai labels so the staff pipeline reads in the same language as the chat's
// customer card (which collapses acknowledged+in_progress into "กำลังทำ" — see
// CLIENT_STAGE_LABEL / the "ลูกค้าเห็น" bridge below).
// Exported — the guest landing view's read-only conversation (docs/
// superpowers/specs/2026-07-23-guest-access-design.md, PR 2) reuses this
// exact label/colour map + EventRow renderer, never a rebuilt copy.
export const STATUS_META: Record<SubmissionStatus, { label: string; color: string }> = {
  draft:        { label: "ร่าง",       color: "#6E7585" },
  submitted:    { label: "ส่งแล้ว",     color: "#1F4E79" },
  acknowledged: { label: "รับทราบ",    color: "#2563EB" },
  // in_progress stays LITERAL, deliberately. It was #D97706 and needs_info was
  // #B45309 — two visibly different ambers. The handoff supplies exactly one
  // (`warn`), so routing both through --warn would render them identically in
  // StatusPill and lose a distinction the pipeline track relies on. The likely
  // answer is --accent (which the track already uses for "active" below), but
  // that is a palette decision, not an audit one.
  in_progress:  { label: "กำลังทำ",    color: "#D97706" },
  needs_info:   { label: "รอข้อมูล",    color: "var(--warn)" },
  completed:    { label: "เสร็จ",      color: "var(--ok)" },
  cancelled:    { label: "ยกเลิก",     color: "#8A8F98" },
};
const PIPELINE: SubmissionStatus[] = ["submitted", "acknowledged", "in_progress", "completed"];
// What the CUSTOMER's chat card shows for this submission right now — the
// 4-state internal pipeline collapses to 3 client stages (deliberate: a
// customer doesn't care about acknowledged-vs-in-progress plumbing). This
// bridge lets staff answer "ลูกค้าเห็นสถานะอะไร" at a glance.
const CLIENT_STAGE_LABEL: Record<string, string> = {
  received: "ได้รับแล้ว", processing: "กำลังทำ", done: "เสร็จ", cancelled: "ยกเลิก",
};
const TRANSITION_LABEL: Record<string, string> = {
  submitted: "Submit to team", acknowledged: "Acknowledge", in_progress: "Start work",
  needs_info: "Request info", completed: "Complete", cancelled: "Cancel",
};
// The single natural forward move from each stage — drives the ONE primary
// button. Everything else the backend allows (skip-to-complete, needs_info,
// cancel) is offered as a quiet secondary action.
const NEXT_TRANSITION: Record<SubmissionStatus, SubmissionStatus | undefined> = {
  draft: "submitted", submitted: "acknowledged", acknowledged: "in_progress",
  in_progress: "completed", needs_info: "in_progress", completed: undefined, cancelled: undefined,
};
const NEXT_HINT: Record<string, string> = {
  draft: "Send it to the team", submitted: "Take this on to start handling",
  acknowledged: "Begin the work", in_progress: "Mark done when the work is finished",
  needs_info: "Resume once the customer replies",
};
export const INTENT_LABEL: Record<string, string> = {
  import: "Import data", organize: "File & organize", qa: "Review / QA", store: "Store", other: "Other",
};

// Parses the ISO string as a Date (not a string-slice) so it renders in the
// viewer's local time, matching how ageDays/isOverdue already parse. String-
// slicing raw UTC digits was wrong even after the backend started emitting
// +00:00 offsets (7h off for Thai users vs. actual local time).
export function fmtWhen(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

export function isOverdue(s: { due_at: string | null; status: SubmissionStatus }): boolean {
  return !!s.due_at && s.status !== "completed" && s.status !== "cancelled" && Date.parse(s.due_at) < Date.now();
}

// Exported — same reuse reason as STATUS_META/EventRow above (the guest
// landing view's submission header shows the identical pill).
export function StatusPill({ status, small }: { status: SubmissionStatus; small?: boolean }): JSX.Element {
  const m = STATUS_META[status];
  return (
    <span className="font-mono uppercase inline-flex items-center" style={{
      fontSize: small ? 8.5 : 9.5, letterSpacing: ".1em", fontWeight: 700, color: m.color,
      border: `1px solid ${m.color}`, borderRadius: 3, padding: small ? "1px 5px" : "2px 7px",
      background: tint(m.color, 8) }}>{m.label}</span>
  );
}

type PatchBody = { assigned_to_id?: number; clear_assignee?: boolean; due_at?: string; clear_due?: boolean; priority?: string };

// The workspace's data + lifecycle actions for one job. A hook, not a copy:
// SubmissionsScreen's detail pane and this screen must fire the same queries,
// under the same keys, and invalidate the same things — otherwise the two
// surfaces drift while the code looks identical.
export function useJobWorkspace(id: number | null, isStaff: boolean): {
  detailQ: UseQueryResult<SubmissionDetail>;
  assigneesQ: UseQueryResult<{ id: number; name: string }[]>;
  patch: UseMutationResult<SubmissionDetail, unknown, PatchBody>;
  transition: UseMutationResult<SubmissionDetail, unknown, { to: string; outcome?: string; assigned_to_id?: number }>;
  act: (to: SubmissionStatus) => void;
} {
  const qc = useQueryClient();
  const me = getCachedUser();
  const detailQ = useQuery({
    queryKey: ["submission", id], queryFn: () => apiClient.submission(id!),
    enabled: id != null, staleTime: 10_000,
  });
  const assigneesQ = useQuery({
    queryKey: ["sub-assignees"], queryFn: () => apiClient.submissionAssignees(),
    enabled: isStaff, staleTime: 300_000,
  });
  const patch = useMutation({
    mutationFn: (v: PatchBody) => apiClient.submissionPatch(id!, v),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["submission", id] }); qc.invalidateQueries({ queryKey: ["submissions"] }); },
  });
  const transition = useMutation({
    mutationFn: (v: { to: string; outcome?: string; assigned_to_id?: number }) => apiClient.submissionTransition(id!, v),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["submissions"] }); qc.invalidateQueries({ queryKey: ["submission", id] }); },
  });
  const act = (to: SubmissionStatus): void => {
    if (to === "completed") {
      const outcome = window.prompt("Completion summary (what was done)", "") ?? undefined;
      transition.mutate({ to, outcome, assigned_to_id: isStaff && me ? me.id : undefined });
    } else if (to === "cancelled") {
      if (confirm("Cancel this submission?")) transition.mutate({ to });
    } else {
      transition.mutate({ to, assigned_to_id: isStaff && me ? me.id : undefined });
    }
  };
  return { detailQ, assigneesQ, patch, transition, act };
}

export default function JobScreen(): JSX.Element {
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const isStaff = (STAFF_ROLES as readonly string[]).includes(getCachedUser()?.role ?? "");
  // A job id that is not a number is not a job. Resolving it to NaN and asking
  // the API anyway is how a typo becomes a 422 in the log and a blank frame on
  // the screen.
  const parsed = Number(idParam);
  const id = idParam != null && idParam !== "" && Number.isInteger(parsed) && parsed > 0 ? parsed : null;

  // The job workspace is the team's tool; customers live in the delivery feed
  // on the File manager instead. (Moved from SubmissionsScreen:150.)
  useEffect(() => { if (!isStaff) navigate("/files/queue", { replace: true }); }, [isStaff]);   // eslint-disable-line react-hooks/exhaustive-deps

  const { detailQ, assigneesQ, patch, transition, act } = useJobWorkspace(isStaff ? id : null, isStaff);

  // Rendered nothing rather than a flash of the workspace: the effect above is
  // already navigating away, and a customer should never see one frame of a
  // staff screen on the way out.
  if (!isStaff) return <></>;

  if (id == null || detailQ.isError) return <NotFound />;
  if (detailQ.isPending || !detailQ.data) {
    return (
      <div className="absolute inset-0 flex items-center justify-center font-mono"
           style={{ background: "var(--paper)", fontSize: 12, color: "var(--ink-3)" }}>Loading…</div>
    );
  }
  const d = detailQ.data;

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden" style={{ background: "var(--paper)" }}>
      <div className="px-6 py-2.5 border-b flex items-center justify-between gap-4" style={{ borderColor: "var(--hairline)" }}>
        <div className="font-mono uppercase flex items-center gap-2" style={{ fontSize: 10.5, letterSpacing: ".22em", color: "var(--ink-3)" }}>
          <Link to="/today" className="inline-flex items-center gap-1.5 hover:text-[var(--accent-deep)] transition-colors">
            <ArrowLeft size={11} /> WORK QUEUE
          </Link>
          <span className="opacity-40">/</span>
          <span style={{ color: "var(--accent-deep)", fontWeight: 600 }}>JOB · {d.ref}</span>
        </div>
      </div>
      <div className="flex-1 min-w-0 overflow-auto">
        <Detail d={d} isStaff={isStaff} onAct={act} busy={transition.isPending}
                assignees={assigneesQ.data ?? []} onPatch={(v) => patch.mutate(v)} patching={patch.isPending} />
      </div>
    </div>
  );
}

// An id nobody can resolve lands here rather than on an empty frame — the same
// shape InspectionDetailScreen uses for an inspection that isn't there.
function NotFound(): JSX.Element {
  return (
    <div className="absolute inset-0 flex items-center justify-center font-mono text-center px-6"
         style={{ background: "var(--paper)", fontSize: 12, color: "var(--err)" }}>
      <div>
        Job not found.{" "}
        <Link to="/today" className="underline" style={{ color: "var(--accent-deep)" }}>Back to the work queue</Link>
      </div>
    </div>
  );
}

// ── Detail workspace — progressive disclosure: lead with the ONE next action
// and the conversation; tuck ownership + provenance out of the way. ──
export function Detail({ d, isStaff, onAct, busy, assignees, onPatch, patching }: {
  d: SubmissionDetail; isStaff: boolean; onAct: (to: SubmissionStatus) => void; busy: boolean;
  assignees: { id: number; name: string }[]; onPatch: (v: PatchBody) => void; patching: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const [editMeta, setEditMeta] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const stageIdx = PIPELINE.indexOf(d.status === "needs_info" ? "in_progress" : d.status);
  const overdue = isOverdue(d);
  const primary = NEXT_TRANSITION[d.status];
  const hasPrimary = !!primary && d.allowed_transitions.includes(primary);
  const secondary = d.allowed_transitions.filter((t) => t !== (hasPrimary ? primary : undefined) && t !== "cancelled");
  const canCancel = d.allowed_transitions.includes("cancelled");

  return (
    <div className="p-6 max-w-3xl">
      {/* header */}
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>
          {d.ref}{d.priority === "urgent" && <span style={{ color: "var(--err)", fontWeight: 700 }}> · URGENT</span>}
          {overdue && <span style={{ color: "var(--err)", fontWeight: 700 }}> · OVERDUE</span>}
        </div>
        <div className="flex items-center gap-2">
          {/* Guest access (workbench roadmap D2) — scoped + expiring
              third-party link to this submission. Staff only. */}
          {isStaff && <GuestInvitePanel scopeType="submission" scopeRef={d.id} defaultLabel={d.title} />}
          <StatusPill status={d.status} />
        </div>
      </div>
      <h2 className="font-display font-medium m-0 mb-4" style={{ fontSize: 22, color: "var(--ink-1)" }}>{d.title}</h2>

      {/* PRIMARY next action — the one thing to do now */}
      {hasPrimary && (
        <div className="flex items-center gap-3 mb-2.5 px-3.5 py-2.5" style={{ background: "rgba(var(--accent-rgb) / 0.08)", border: "1px solid rgba(var(--accent-rgb) / 0.35)" }}>
          <ArrowRight size={16} style={{ color: "var(--accent)", flexShrink: 0 }} />
          <span className="font-mono flex-1" style={{ fontSize: 12, color: "var(--ink-2)" }}>
            Next: {NEXT_HINT[d.status] ?? TRANSITION_LABEL[primary!]}
          </span>
          <button disabled={busy} onClick={() => onAct(primary!)}
                  className="font-mono uppercase px-4 py-1.5 transition hover:brightness-95 active:translate-y-px disabled:opacity-40 whitespace-nowrap"
                  style={{ fontSize: 10, letterSpacing: ".1em", fontWeight: 700,
                           color: primary === "completed" ? "#fff" : "var(--ink-1)",
                           background: primary === "completed" ? "var(--ok)" : "var(--accent)",
                           border: `1px solid ${primary === "completed" ? "var(--ok)" : "var(--accent)"}`,
                           boxShadow: "0 1px 2px rgba(10,15,24,0.15)" }}>
            {TRANSITION_LABEL[primary!]}
          </button>
        </div>
      )}
      {/* secondary actions — quiet */}
      {(secondary.length > 0 || canCancel) && (
        <div className="flex items-center gap-2 mb-4">
          {secondary.map((t) => (
            <button key={t} disabled={busy} onClick={() => onAct(t)}
                    className="font-mono uppercase px-2.5 py-1 border transition hover:bg-[rgba(31,78,121,0.05)] active:translate-y-px disabled:opacity-40"
                    style={{ fontSize: 9, letterSpacing: ".08em", color: "var(--ink-3)", borderColor: "var(--hairline)" }}>
              {TRANSITION_LABEL[t] ?? t}
            </button>
          ))}
          {canCancel && (
            <button disabled={busy} onClick={() => onAct("cancelled")}
                    className="font-mono uppercase px-2.5 py-1 border transition hover:bg-[rgba(192,57,43,0.06)] active:translate-y-px disabled:opacity-40 ml-auto"
                    style={{ fontSize: 9, letterSpacing: ".08em", color: "var(--err)", borderColor: "rgba(192,57,43,0.4)" }}>Cancel</button>
          )}
        </div>
      )}

      {/* compact stage line */}
      {d.status !== "cancelled" && (
        <div className="flex items-center gap-1 mb-4">
          {PIPELINE.map((st, i) => {
            const done = i < stageIdx || d.status === "completed";
            const active = i === stageIdx && d.status !== "completed";
            const c = done ? "var(--ok)" : active ? (d.status === "needs_info" ? STATUS_META.needs_info.color : "var(--accent-deep)") : "var(--ink-4)";
            return (
              <div key={st} className="flex items-center gap-1">
                <span className="font-mono uppercase inline-flex items-center gap-1" style={{ fontSize: 8.5, letterSpacing: ".07em", color: c, fontWeight: active || done ? 700 : 400 }}>
                  {done && <CheckCircle2 size={10} />}{active && d.status === "needs_info" ? "รอข้อมูล" : STATUS_META[st].label}
                </span>
                {i < PIPELINE.length - 1 && <ArrowRight size={10} style={{ color: "var(--ink-4)" }} />}
              </div>
            );
          })}
          {/* The bridge: what the customer's chat card shows for this งาน right
              now (the internal 4 states collapse to 3 client stages). */}
          <span className="font-mono" style={{ fontSize: 9, color: "var(--ink-4)", marginLeft: 6 }}>
            · ลูกค้าเห็น: <span style={{ color: "var(--ink-2)", fontWeight: 700 }}>
              {CLIENT_STAGE_LABEL[d.client_stage] ?? d.client_stage}
            </span>
          </span>
        </div>
      )}

      {/* ownership — collapsed to one line; expand to edit (staff) */}
      {isStaff && d.status !== "cancelled" && (
        editMeta ? (
          <div className="flex items-center gap-2 mb-4 flex-wrap font-mono" style={{ fontSize: 11 }}>
            <span className="uppercase" style={{ fontSize: 8.5, letterSpacing: ".14em", color: "var(--ink-4)" }}>Assignee</span>
            <select value={d.assigned_to_id ?? ""}
                    onChange={(e) => onPatch(e.target.value ? { assigned_to_id: Number(e.target.value) } : { clear_assignee: true })}
                    disabled={patching}
                    className="px-2 py-1 outline-none" style={{ background: "var(--paper-2)", border: "1px solid var(--hairline)", color: "var(--ink-1)", fontSize: 11 }}>
              <option value="">— unassigned —</option>
              {assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <span className="uppercase ml-2" style={{ fontSize: 8.5, letterSpacing: ".14em", color: "var(--ink-4)" }}>Due</span>
            <input type="date" value={d.due_at ? d.due_at.slice(0, 10) : ""} disabled={patching}
                   onChange={(e) => onPatch(e.target.value ? { due_at: e.target.value } : { clear_due: true })}
                   className="px-2 py-1 outline-none" style={{ background: "var(--paper-2)", border: `1px solid ${overdue ? "var(--err)" : "var(--hairline)"}`, color: overdue ? "var(--err)" : "var(--ink-1)", fontSize: 11 }} />
            <button onClick={() => onPatch({ priority: d.priority === "urgent" ? "normal" : "urgent" })} disabled={patching}
                    className="uppercase px-2 py-1 border transition-colors ml-2"
                    style={{ fontSize: 8.5, letterSpacing: ".1em", fontWeight: 700,
                             color: d.priority === "urgent" ? "var(--err)" : "var(--ink-3)",
                             borderColor: d.priority === "urgent" ? "var(--err)" : "var(--hairline)",
                             background: d.priority === "urgent" ? "rgba(192,57,43,0.06)" : "transparent" }}>
              {d.priority === "urgent" ? "★ Urgent" : "Mark urgent"}
            </button>
            <button onClick={() => setEditMeta(false)} className="font-mono ml-1" style={{ fontSize: 9.5, color: "var(--accent-deep)" }}>Done</button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 mb-4 font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>
            <span>{d.assigned_to ? `@${d.assigned_to}` : "Unassigned"}</span><span style={{ color: "var(--ink-3)" }}>·</span>
            <span style={{ color: overdue ? "var(--err)" : "var(--ink-3)" }}>{d.due_at ? `Due ${fmtWhen(d.due_at)}` : "No due date"}</span>
            <span style={{ color: "var(--ink-4)" }}>·</span>
            <span style={{ color: d.priority === "urgent" ? "var(--err)" : "var(--ink-3)" }}>{d.priority === "urgent" ? "Urgent" : "Normal"}</span>
            <button onClick={() => setEditMeta(true)} className="inline-flex items-center gap-1 ml-2" style={{ fontSize: 9.5, color: "var(--accent-deep)" }}><Pencil size={10} /> Edit</button>
          </div>
        )
      )}

      {/* outcome — the result, kept visible */}
      {d.status === "completed" && d.outcome && (
        <div className="mb-5 px-3 py-2.5 font-mono" style={{ fontSize: 11.5, color: "var(--ok)", background: "rgba(21,128,61,.06)", border: "1px solid rgba(21,128,61,.35)" }}>
          <div className="uppercase mb-0.5" style={{ fontSize: 8.5, letterSpacing: ".18em" }}>Outcome</div>{d.outcome}
        </div>
      )}

      {/* files */}
      <div className="mb-5">
        <div className="font-mono uppercase mb-2" style={{ fontSize: 8.5, letterSpacing: ".18em", color: "var(--ink-4)" }}>Files · {d.files.length}</div>
        {d.files.length === 0 ? (
          <div className="font-mono px-3 py-3 text-center" style={{ fontSize: 11, color: "var(--ink-3)", border: "1px solid var(--hairline)" }}>No files bundled</div>
        ) : (
          <div style={{ border: "1px solid var(--hairline)" }}>
            {d.files.map((f: IntakeFile) => (
              <div key={f.id} className="flex items-center gap-2.5 px-3 py-2 border-b last:border-b-0" style={{ borderColor: "var(--hairline)" }}>
                <FileText size={14} style={{ color: "var(--ink-3)" }} />
                {f.download_url
                  ? <a href={f.download_url} target="_blank" rel="noreferrer" className="font-mono truncate hover:underline flex-1" style={{ fontSize: 12, color: "var(--ink-1)" }}>{f.filename}</a>
                  : <span className="font-mono truncate flex-1" style={{ fontSize: 12, color: "var(--ink-1)" }}>{f.filename}</span>}
                <span className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: ".06em", color: f.status === "imported" ? "var(--ok)" : f.asset_id ? "#2563EB" : "var(--ink-4)" }}>
                  {f.status === "imported" ? "imported" : f.asset_tag ? `→ ${f.asset_tag}` : "received"}
                </span>
              </div>
            ))}
          </div>
        )}
        {isStaff && d.files.length > 0 && (
          <button onClick={() => navigate("/files/queue")} className="font-mono uppercase mt-2 inline-flex items-center gap-1 px-2 py-1 border transition hover:bg-[rgba(31,78,121,0.05)]"
                  style={{ fontSize: 9, letterSpacing: ".08em", color: "var(--ink-3)", borderColor: "var(--hairline)" }}>
            <ExternalLink size={11} /> Open File manager
          </button>
        )}
      </div>

      {/* conversation — the working surface */}
      <Timeline subId={d.id} isStaff={isStaff} />

      {/* provenance — one click away */}
      <button onClick={() => setShowDetails((v) => !v)}
              className="font-mono uppercase inline-flex items-center gap-1 mt-6 mb-2" style={{ fontSize: 8.5, letterSpacing: ".18em", color: "var(--ink-4)" }}>
        {showDetails ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Details
      </button>
      {showDetails && (
        <div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 font-mono" style={{ fontSize: 11.5 }}>
            <Meta label="Intent" value={INTENT_LABEL[d.intent] ?? d.intent} />
            <Meta label="Requested by" value={d.created_by || "—"} />
            {d.plant_name && <Meta label="Plant" value={d.plant_name} icon={<Building2 size={11} />} />}
            <Meta label="Assigned to" value={d.assigned_to ? `@${d.assigned_to}` : "—"} />
            <Meta label="Submitted" value={fmtWhen(d.submitted_at)} />
            <Meta label="Completed" value={fmtWhen(d.completed_at)} />
          </div>
          {d.description && (
            <div className="mb-2">
              <div className="font-mono uppercase mb-1" style={{ fontSize: 8.5, letterSpacing: ".18em", color: "var(--ink-4)" }}>Request</div>
              <p className="font-mono" style={{ fontSize: 12, color: "var(--ink-2)", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{d.description}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Activity timeline + compose (messages · notes · status/system events) ──
function Timeline({ subId, isStaff }: { subId: number; isStaff: boolean }): JSX.Element {
  const qc = useQueryClient();
  const evQ = useQuery({ queryKey: ["sub-events", subId], queryFn: () => apiClient.submissionEvents(subId), staleTime: 5_000 });
  const [text, setText] = useState("");
  const [internal, setInternal] = useState(false);
  const post = useMutation({
    mutationFn: () => apiClient.submissionPostMessage(subId, { body: text, is_internal: internal }),
    onSuccess: () => {
      setText("");
      qc.invalidateQueries({ queryKey: ["sub-events", subId] });
      qc.invalidateQueries({ queryKey: ["submission", subId] });
      qc.invalidateQueries({ queryKey: ["submissions"] });
    },
  });
  const events = evQ.data ?? [];
  return (
    <div className="mt-7 pt-4 border-t" style={{ borderColor: "var(--hairline)" }}>
      <div className="font-mono uppercase mb-3" style={{ fontSize: 8.5, letterSpacing: ".18em", color: "var(--ink-4)" }}>Activity</div>
      <div className="flex flex-col gap-2.5 mb-4">
        {events.map((e) => <EventRow key={e.id} e={e} />)}
        {events.length === 0 && <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-3)" }}>No activity yet</div>}
      </div>
      <div style={{ border: "1px solid var(--hairline)", background: internal ? "rgba(var(--accent-rgb) / 0.05)" : "var(--paper-2)" }}>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
                  placeholder={internal ? "Internal note (customer won't see this)…" : "Write a message…"}
                  className="w-full font-mono px-3 py-2 outline-none resize-none bg-transparent" style={{ fontSize: 12, color: "var(--ink-1)" }} />
        <div className="flex items-center justify-between px-3 py-1.5 border-t" style={{ borderColor: "var(--hairline)" }}>
          {isStaff ? (
            <label className="font-mono uppercase inline-flex items-center gap-1.5 cursor-pointer" style={{ fontSize: 9, letterSpacing: ".08em", color: internal ? "var(--accent-deep)" : "var(--ink-4)" }}>
              <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} style={{ accentColor: "var(--accent)" }} /> Internal note
            </label>
          ) : <span />}
          <button onClick={() => post.mutate()} disabled={!text.trim() || post.isPending}
                  className="font-mono uppercase inline-flex items-center gap-1.5 px-3 py-1 transition hover:brightness-95 active:translate-y-px disabled:opacity-40"
                  style={{ fontSize: 9.5, letterSpacing: ".1em", color: "var(--ink-1)", background: "var(--accent)", border: "1px solid var(--accent)", fontWeight: 700 }}>
            <Send size={11} /> Send
          </button>
        </div>
      </div>
    </div>
  );
}

export function EventRow({ e }: { e: SubmissionEvent }): JSX.Element {
  if (e.kind === "status" || e.kind === "system") {
    const to = (e.meta?.to as string) ?? "";
    const label = e.kind === "status" && to ? `moved to ${STATUS_META[to as SubmissionStatus]?.label ?? to}` : e.body ?? "";
    return (
      <div className="flex items-center gap-2 font-mono" style={{ fontSize: 10, color: "var(--ink-4)" }}>
        <span style={{ height: 1, width: 18, background: "var(--hairline)" }} />
        <span><b style={{ color: "var(--ink-3)", fontWeight: 600 }}>{e.actor_name}</b> {label} · {fmtWhen(e.created_at)}</span>
      </div>
    );
  }
  const staff = e.actor_role === "staff";
  const note = e.kind === "note";
  return (
    <div className="px-3 py-2" style={{
      border: `1px solid ${note ? "rgba(var(--accent-rgb) / 0.4)" : "var(--hairline)"}`,
      background: note ? "rgba(var(--accent-rgb) / 0.06)" : staff ? "rgba(31,78,121,0.04)" : "var(--paper-2)" }}>
      <div className="flex items-center gap-2 mb-1 font-mono" style={{ fontSize: 9.5 }}>
        <span style={{ color: "var(--ink-2)", fontWeight: 600 }}>{e.actor_name}</span>
        <span className="uppercase" style={{ fontSize: 8, letterSpacing: ".08em", color: staff ? "var(--accent-deep)" : "var(--ink-4)" }}>{staff ? "TEAM" : "CLIENT"}</span>
        {note && <span className="uppercase" style={{ fontSize: 8, letterSpacing: ".08em", color: "var(--accent-deep)", border: "1px solid var(--accent)", borderRadius: 3, padding: "0 4px" }}>INTERNAL</span>}
        <span className="ml-auto" style={{ color: "var(--ink-4)" }}>{fmtWhen(e.created_at)}</span>
      </div>
      <div className="font-mono" style={{ fontSize: 12, color: "var(--ink-1)", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{e.body}</div>
    </div>
  );
}

function Meta({ label, value, icon }: { label: string; value: string; icon?: JSX.Element }): JSX.Element {
  return (
    <div>
      <div className="uppercase" style={{ fontSize: 8.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>{label}</div>
      <div className="inline-flex items-center gap-1" style={{ color: "var(--ink-2)" }}>{icon}{value}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// CreateModal — opening a งาน from nothing.
//
// MOVED here from SubmissionsScreen on 2026-09-01 when that screen retired
// (spec: TODAY is the queue). It is the only free-form way to open a งาน:
// the file room's "บันทึกรอบส่ง" creates one FROM selected files, and the
// chat dock's "ขอให้ตรวจ/ประเมิน" creates one from a conversation, but
// neither takes a title, an intent or a priority. Deleting the screen
// without moving this would have retired a capability rather than a route,
// which is the one thing a route retirement must not do.
//
// It lives beside the JOB screen because it produces one — INTENT_LABEL and
// the งาน vocabulary are already here. TODAY opens it; the caller decides
// where to go with the งาน it returns.
//
// The `fileIds` prop is kept: a caller can pre-bundle files into the new งาน.
// ────────────────────────────────────────────────────────────────────
export function CreateModal({ onClose, onCreated, fileIds }: {
  onClose: () => void; onCreated: (d: SubmissionDetail) => void; fileIds?: number[];
}): JSX.Element {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [intent, setIntent] = useState("import");
  const [priority, setPriority] = useState("normal");
  const create = useMutation({
    mutationFn: (submit: boolean) => apiClient.submissionCreate({ title, description, intent, priority, file_ids: fileIds ?? [], submit }),
    onSuccess: (d) => onCreated(d),
  });
  const ready = title.trim().length > 0;
  return (
    <div className="fixed inset-0 z-[1200] flex items-start justify-center pt-[12vh]" style={{ background: "rgba(10,15,24,0.5)" }} onClick={onClose}>
      <div className="w-[520px] max-w-[94vw] shadow-2xl" onClick={(e) => e.stopPropagation()} style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }}>
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--hairline)" }}>
          <div className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: ".16em", color: "var(--ink-3)" }}>
            / NEW SUBMISSION{fileIds?.length ? ` · ${fileIds.length} files` : ""}
          </div>
          <button onClick={onClose} aria-label="ปิด"><X size={14} style={{ color: "var(--ink-3)" }} /></button>
        </div>
        <div className="p-4 flex flex-col gap-3">
          <Field label="Title">
            <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Q2 2026 UT survey — Tank Farm A"
                   className="w-full font-mono px-2 py-2 outline-none" style={{ fontSize: 13, color: "var(--ink-1)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }} />
          </Field>
          <Field label="What do you need?">
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Describe the work for the XZYNEXA team…"
                      className="w-full font-mono px-2 py-2 outline-none resize-none" style={{ fontSize: 12, color: "var(--ink-1)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }} />
          </Field>
          <div className="flex gap-3">
            <Field label="Intent">
              <select value={intent} onChange={(e) => setIntent(e.target.value)} className="font-mono px-2 py-1.5 outline-none" style={{ fontSize: 12, color: "var(--ink-1)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
                {Object.entries(INTENT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className="font-mono px-2 py-1.5 outline-none" style={{ fontSize: 12, color: "var(--ink-1)", background: "var(--paper-2)", border: "1px solid var(--hairline)" }}>
                <option value="normal">Normal</option><option value="urgent">Urgent</option>
              </select>
            </Field>
          </div>
          {create.isError && <div className="font-mono" style={{ fontSize: 11, color: "var(--err)" }}>Could not create — check the fields.</div>}
          <div className="flex items-center gap-2 justify-end pt-1">
            <button onClick={() => create.mutate(false)} disabled={!ready || create.isPending}
                    className="font-mono uppercase px-3 py-1.5 border transition hover:bg-[rgba(31,78,121,0.05)] active:translate-y-px disabled:opacity-40"
                    style={{ fontSize: 9.5, letterSpacing: ".1em", color: "var(--ink-3)", borderColor: "var(--hairline)" }}>Save draft</button>
            <button onClick={() => create.mutate(true)} disabled={!ready || create.isPending}
                    className="font-mono uppercase px-3.5 py-1.5 transition hover:brightness-95 active:translate-y-px disabled:opacity-40"
                    style={{ fontSize: 9.5, letterSpacing: ".1em", color: "var(--ink-1)", background: "var(--accent)", border: "1px solid var(--accent)", fontWeight: 700 }}>
              {create.isPending ? "…" : "Submit to team"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono uppercase" style={{ fontSize: 8.5, letterSpacing: ".16em", color: "var(--ink-4)" }}>{label}</span>
      {children}
    </label>
  );
}
